import React from "react"
import { Folder, RefreshCw, Hash, Link2, Lock } from "lucide-react"
import { fetchWithAuth } from "@/lib/api"
import type { Project, SearchResponse, SearchResult } from "@/lib/api/documents"
import type { Workspace } from "@/providers/workspace-provider"
import { workspaceBadgeFromPath } from "@/lib/workspace-path-match"
import type { CommandItem } from "./command-palette.types"
import { getDocumentIcon, extractBasecampUrlInfo } from "./command-palette.utils"
import { parseQuery } from "@/lib/search/query-parser"
import { filterWorkspaceIdsForSearch } from "@/lib/open-tab-ids-for-search"
import { extractMaaterAppRoute, extractMaaterDocumentUrlInfo } from "@/lib/maater-document-url"
import { buildMaaterAppRouteRows, isNonDocumentMaaterRoute } from "./command-palette.maater-routes"
import { extractGoogleDriveUrlInfo } from "@/lib/google-drive-url"
import { paletteTodoIdFromDocument } from "@/lib/todo-id-from-path"
import { buildGoogleDriveCommandRows } from "./command-palette.google-drive"
import {
  documentUuidFromPathOrId,
  formatUserFacingStoragePath,
  isUuid,
  resolveStoragePathForFetch,
  storagePathProbeCandidates,
  workspaceForStoragePath,
  type WorkspaceRouteInfo,
} from "@/lib/app-routes"
import { titleHintFromPathOrId } from "@/lib/document-deep-link-cold-load"

function searchErrorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message
  return "Search failed"
}

/** Document row from search API plus optional client-side ranking overrides. */
export type CommandSearchHit = SearchResult & {
  _matchType?: string
  _matchScore?: number
  frontmatter?: Record<string, unknown>
}

type CommandPaletteDocResults = {
  results: CommandSearchHit[]
}

type BasecampTodoDocument = Pick<SearchResult, "id" | "title" | "type"> &
  Partial<Omit<SearchResult, "id" | "title" | "type">> & {
    frontmatter?: Record<string, unknown>
  }

interface BasecampTodoDocumentsResponse {
  documents?: BasecampTodoDocument[]
}

const EMPTY_DOC_RESULTS: CommandPaletteDocResults = { results: [] }

function toCommandSearchHit(
  doc: BasecampTodoDocument,
  overrides: Pick<CommandSearchHit, "_matchType" | "_matchScore">,
): CommandSearchHit {
  return {
    id: doc.id,
    title: doc.title,
    type: doc.type,
    excerpt: doc.excerpt ?? "",
    tags: doc.tags ?? [],
    matchScore: doc.matchScore ?? 90,
    projectName: doc.projectName,
    updatedBy: doc.updatedBy ?? "",
    updatedAt: doc.updatedAt ?? "",
    filePath: doc.filePath,
    frontmatter: doc.frontmatter,
    ...overrides,
  }
}

export type FetchWithAuthFn = typeof fetchWithAuth

export interface ExecuteCommandPaletteSearchParams {
  trimmedSearch: string
  isUrl: boolean
  isId: boolean
  isNumericId: boolean
  isBasecampId: boolean
  extractedFilename: string | null
  basecampIdFromFilename: string | null
  basecampId: string | null
  basecampType: "todo" | "message" | "inbox_forward" | null
  /** Boost ranking for open document tabs when supported by API */
  openTabDocumentIds?: string[]
  /** Boost ranking for recently viewed documents when supported by API */
  recentlyViewedDocumentIds?: string[]
  /** Active workspace content roots — restricts search to enabled workspaces */
  folderPaths?: string[]
  /** Active workspace org UUIDs — sent alongside folderPaths for correct multi-org filtering */
  workspaceIds?: string[]
  /** Globally active workspace IDs (gate Maater URL open when workspace is off) */
  activeWorkspaceIds?: string[]
  /** Abort in-flight search when query changes */
  signal?: AbortSignal
  /** Return false to discard stale results from a superseded search */
  isCurrentSearch?: () => boolean
  /** For per-result workspace badges */
  workspaces?: Workspace[]
  fetchWithAuthFn?: FetchWithAuthFn
}

export interface ExecuteCommandPaletteSearchResult {
  allResults: CommandItem[]
  hasDocumentMatches: boolean
  /** When true, caller should set selected index to `(isUrl ? 1 : 0) + (isId ? 1 : 0)` */
  autoSelectFirstFilenameMatch: boolean
  /** Prefer selecting first document row (e.g. pasted Basecamp URL) */
  preferredDocumentIndex?: number
  /** Set when document or project search failed (palette should show error, not spin forever) */
  searchError?: string | null
}

function mergeTodoDocumentsIntoResults(
  docResults: CommandPaletteDocResults,
  basecampData: BasecampTodoDocumentsResponse,
) {
  if (!basecampData.documents?.length) return
  const existingIds = new Set(docResults.results.map((r) => r.id))
  const newResults = basecampData.documents
    .filter((doc) => !existingIds.has(doc.id))
    .map((doc) =>
      toCommandSearchHit(doc, {
        _matchType: "basecamp-id",
        // Preserve the backend's per-doc rank (exact todo_id/path match ~90-105)
        // instead of a flat 95 for everything — when the todolist-folder fallback
        // legitimately expands results (see todo-service.js _extractTodolistIdFromPaths),
        // this keeps the actual matched doc ranked above unrelated sibling docs
        // instead of falling back to alphabetical sort on a tie.
        _matchScore: doc.matchScore ?? 95,
      }),
    )
  docResults.results = [...docResults.results, ...newResults]
}

function getActionLabels(type: string) {
  switch (type) {
    case "message":
      return {
        update: "🔄 Update Message from Basecamp",
        ingest: "📥 Ingest Basecamp Message",
        subtitle: "Create new markdown file from Basecamp message",
      }
    case "inbox_forward":
      return {
        update: "🔄 Update Email Forward from Basecamp",
        ingest: "📥 Ingest Basecamp Email Forward",
        subtitle: "Create new markdown file from Basecamp email forward",
      }
    default:
      return {
        update: "🔄 Update Work Spec from Basecamp",
        ingest: "📥 Ingest Basecamp Todo",
        subtitle: "Create new markdown file from Basecamp todo",
      }
  }
}

function toWorkspaceRouteInfo(workspaces: Workspace[]): WorkspaceRouteInfo[] {
  return workspaces.map((w) => ({
    id: w.id,
    slug: w.slug,
    name: w.name,
    contentRoot: w.contentRoot,
  }))
}

function titleFromDocumentJson(
  doc: { title?: string; filePath?: string; file_path?: string; path?: string },
  fallbackId: string,
): string {
  const filePath = doc.filePath || doc.file_path || doc.path || ''
  const fromTitle = typeof doc.title === 'string' ? doc.title.trim() : ''
  if (fromTitle && !isUuid(fromTitle)) return fromTitle
  if (filePath) return titleHintFromPathOrId(filePath)
  return titleHintFromPathOrId(fallbackId)
}

async function resolveMaaterDocumentByPath(
  pathOrId: string,
  workspaces: Workspace[],
  auth: FetchWithAuthFn,
  signal?: AbortSignal,
): Promise<{ id: string; title: string; type: string; filePath: string } | null> {
  const trimmedId = pathOrId.replace(/^\/+/, '')
  const asUuid = documentUuidFromPathOrId(trimmedId)
  if (asUuid) {
    if (signal?.aborted) return null
    try {
      const res = await auth(
        `/api/documents/${asUuid}`,
        signal ? { signal } : undefined,
      )
      if (res.ok) {
        const doc = (await res.json()) as {
          id?: string
          title?: string
          type?: string
          filePath?: string
          file_path?: string
          path?: string
        }
        if (doc.id) {
          const filePath = doc.filePath || doc.file_path || doc.path || ''
          return {
            id: doc.id,
            title: titleFromDocumentJson(doc, trimmedId),
            type: doc.type || 'note',
            filePath,
          }
        }
      }
    } catch {
      // Fall through to by-path (no-op for a bare UUID)
    }
    return null
  }

  const routeInfo = toWorkspaceRouteInfo(workspaces)
  const storagePath = resolveStoragePathForFetch(pathOrId, routeInfo)
  const candidates = storagePathProbeCandidates(storagePath.replace(/^\/+/, ''))
  for (const candidate of candidates) {
    if (signal?.aborted) return null
    try {
      const res = await auth(
        `/api/documents/by-path?path=${encodeURIComponent(candidate)}`,
        signal ? { signal } : undefined,
      )
      if (!res.ok) continue
      const doc = (await res.json()) as {
        id?: string
        title?: string
        type?: string
        filePath?: string
        file_path?: string
        path?: string
      }
      const filePath = doc.filePath || doc.file_path || doc.path || candidate
      if (!doc.id) continue
      return {
        id: doc.id,
        title: titleFromDocumentJson(doc, pathOrId),
        type: doc.type || 'note',
        filePath,
      }
    } catch {
      // try next candidate
    }
  }
  return null
}

function buildMaaterDocumentOpenRows(opts: {
  pathOrId: string
  resolved: { id: string; title: string; type: string; filePath: string } | null
  workspaces: Workspace[]
  activeWorkspaceIds: string[]
}): CommandItem[] {
  const { pathOrId, resolved, workspaces, activeWorkspaceIds } = opts
  const routeInfo = toWorkspaceRouteInfo(workspaces)
  const storageHint = resolved?.filePath || resolveStoragePathForFetch(pathOrId, routeInfo)
  const ws = workspaceForStoragePath(storageHint, routeInfo)
  const pathLabel = formatUserFacingStoragePath(storageHint, routeInfo)
  const title = resolved?.title || titleHintFromPathOrId(pathOrId)
  const openId = resolved?.id || pathOrId
  const workspaceActive =
    !ws?.id || activeWorkspaceIds.length === 0 || activeWorkspaceIds.includes(ws.id)

  if (ws?.id && !workspaceActive) {
    return [
      {
        id: `enable-workspace-open-${ws.id}-${openId}`,
        title: `Enable ${ws.name} to open`,
        type: 'action',
        icon: <Lock className="h-4 w-4" />,
        subtitle: pathLabel,
        action: 'enable-workspace-and-open',
        matchScore: 250,
        matchType: 'maater-url',
        filePath: storageHint,
        enableWorkspaceId: ws.id,
        enableWorkspaceName: ws.name,
        // Stash open target on existingDocId for handler
        existingDocId: openId,
      },
    ]
  }

  return [
    {
      id: openId,
      title: `Open ${title}`,
      type: 'file',
      icon: getDocumentIcon(resolved?.type || 'note'),
      subtitle: pathLabel,
      action: 'open-document',
      matchScore: 250,
      matchType: 'maater-url',
      filePath: storageHint,
      docType: resolved?.type,
      ...(workspaceBadgeFromPath(storageHint, workspaces)
        ? { workspaceBadge: workspaceBadgeFromPath(storageHint, workspaces)! }
        : {}),
    },
  ]
}

/**
 * Document/project search + Basecamp action rows for the command palette.
 */
export async function executeCommandPaletteSearch(
  params: ExecuteCommandPaletteSearchParams,
): Promise<ExecuteCommandPaletteSearchResult> {
  const {
    trimmedSearch,
    isUrl,
    isNumericId,
    isBasecampId,
    extractedFilename,
    basecampIdFromFilename,
    basecampId,
    basecampType,
    openTabDocumentIds = [],
    recentlyViewedDocumentIds = [],
    folderPaths,
    workspaceIds,
    activeWorkspaceIds = [],
    workspaces = [],
    signal,
    isCurrentSearch,
  } = params

  const auth = params.fetchWithAuthFn ?? fetchWithAuth
  const { searchDocuments, searchProjects } = await import("@/lib/api")
  const documentSearchOpts: Record<string, unknown> = {}
  if (openTabDocumentIds.length > 0) documentSearchOpts.openTabIds = openTabDocumentIds
  if (recentlyViewedDocumentIds.length > 0) {
    documentSearchOpts.recentlyViewedIds = recentlyViewedDocumentIds
  }
  if (folderPaths && folderPaths.length > 0) documentSearchOpts.folderPaths = folderPaths
  const scopedWorkspaceIds = filterWorkspaceIdsForSearch(workspaceIds)
  if (scopedWorkspaceIds && scopedWorkspaceIds.length > 0) {
    documentSearchOpts.workspaceIds = scopedWorkspaceIds
  }

  const stale = () => isCurrentSearch && !isCurrentSearch()

  let searchError: string | null = null
  const recordSearchError = (err: unknown) => {
    if (stale()) return
    if (!searchError) searchError = searchErrorMessage(err)
  }

  const runDocumentSearch = async (
    searchParams: Parameters<typeof searchDocuments>[0],
  ): Promise<SearchResponse> => {
    if (signal?.aborted || stale()) {
      return { ...EMPTY_DOC_RESULTS, query: searchParams.query, count: 0 }
    }
    try {
      return await searchDocuments(searchParams, { signal })
    } catch (err) {
      if (signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) {
        return { ...EMPTY_DOC_RESULTS, query: searchParams.query, count: 0 }
      }
      recordSearchError(err)
      console.warn("Document search failed:", err)
      return { ...EMPTY_DOC_RESULTS, query: searchParams.query, count: 0 }
    }
  }

  const maaterAppRoute = extractMaaterAppRoute(trimmedSearch, toWorkspaceRouteInfo(workspaces))
  const maaterUrlInfo = extractMaaterDocumentUrlInfo(trimmedSearch)
  const driveUrlInfo = extractGoogleDriveUrlInfo(trimmedSearch)
  if (driveUrlInfo) {
    const driveRows = await buildGoogleDriveCommandRows({
      url: driveUrlInfo.url,
      fileId: driveUrlInfo.fileId,
      organizationId: scopedWorkspaceIds?.[0] ?? workspaceIds?.[0] ?? workspaces[0]?.id,
      workspaces,
      activeWorkspaceIds,
      auth,
      signal,
    })
    if (stale()) {
      return { allResults: [], hasDocumentMatches: false, autoSelectFirstFilenameMatch: false }
    }
    return {
      allResults: driveRows,
      hasDocumentMatches: driveRows.some(
        (r) => r.action === 'open-document' || r.action === 'enable-workspace-and-open',
      ),
      autoSelectFirstFilenameMatch: false,
      preferredDocumentIndex: driveRows.length > 0 ? 0 : undefined,
    }
  }

  if (maaterAppRoute && isNonDocumentMaaterRoute(maaterAppRoute.intent) && !maaterUrlInfo) {
    const routeRows = await buildMaaterAppRouteRows(maaterAppRoute)
    if (stale()) {
      return { allResults: [], hasDocumentMatches: false, autoSelectFirstFilenameMatch: false }
    }
    return {
      allResults: routeRows,
      hasDocumentMatches: false,
      autoSelectFirstFilenameMatch: false,
      preferredDocumentIndex: routeRows.length > 0 ? 0 : undefined,
    }
  }

  let actualSearchQuery = trimmedSearch
  let searchStrategy: "normal" | "filename-from-path" | "filename-from-id" | "filename-from-url" = "normal"
  let basecampIdToSearch: string | null = null

  // Use parseQuery to handle file: and / prefixes
  const parsedQ = parseQuery(trimmedSearch)
  const isFilePrefixMode = parsedQ.mode === 'file' && !extractedFilename && !(isNumericId || isBasecampId) && !isUrl
  const fileTerms = isFilePrefixMode ? parsedQ.terms : null

  if (maaterUrlInfo) {
    const base = maaterUrlInfo.pathOrId.split('/').pop() || maaterUrlInfo.pathOrId
    actualSearchQuery = `file:${base}`
    searchStrategy = "filename-from-path"
  } else if (fileTerms) {
    // User typed file: or / prefix — search by filename with stripped terms
    actualSearchQuery = `file:${fileTerms}`
    searchStrategy = "filename-from-path"
  } else if (extractedFilename) {
    actualSearchQuery = `file:${extractedFilename}`
    searchStrategy = "filename-from-path"
    basecampIdToSearch = basecampIdFromFilename
    console.log("[Command Palette] Detected full path, searching for filename:", extractedFilename)
    if (basecampIdToSearch) {
      console.log("[Command Palette] Also detected Basecamp ID in filename:", basecampIdToSearch)
    }
  } else if ((isNumericId || isBasecampId) && !isUrl) {
    const searchId = basecampId || trimmedSearch
    actualSearchQuery = `file:${searchId}`
    searchStrategy = "filename-from-id"
    basecampIdToSearch = searchId
  } else if (isUrl && basecampId) {
    actualSearchQuery = `file:${basecampId}`
    searchStrategy = "filename-from-url"
    basecampIdToSearch = basecampId
  }

  let docResults: CommandPaletteDocResults = EMPTY_DOC_RESULTS

  const basecampUrlInfo = isUrl
    ? extractBasecampUrlInfo(trimmedSearch)
    : { projectId: null, todoId: null }
  const isBasecampTodoUrl =
    isUrl && (basecampType || 'todo') === 'todo' && !!basecampId &&
    (trimmedSearch.includes('basecamp.com') || trimmedSearch.includes('/basecamp/'))
  const pastedBasecampUrl = isBasecampTodoUrl ? trimmedSearch : undefined

  if (
    searchStrategy === "filename-from-path" ||
    searchStrategy === "filename-from-id" ||
    searchStrategy === "filename-from-url"
  ) {
    if (searchStrategy === 'filename-from-url' && basecampIdToSearch && basecampType === 'todo') {
      const [todoDocsSettled, fileSearchSettled] = await Promise.allSettled([
        auth(`/api/basecamp/todo-documents/${basecampIdToSearch}?limit=15`),
        runDocumentSearch({
          query: actualSearchQuery,
          limit: 20,
          ...documentSearchOpts,
        }),
      ])

      if (todoDocsSettled.status === 'fulfilled' && todoDocsSettled.value.ok) {
        try {
          const basecampData = (await todoDocsSettled.value.json()) as BasecampTodoDocumentsResponse
          if (basecampData.documents?.length) {
            docResults.results = basecampData.documents.map((doc) =>
              toCommandSearchHit(doc, {
                _matchType: "basecamp-id",
                _matchScore: 98,
              }),
            )
          }
        } catch (err) {
          console.warn('Basecamp todo-documents parse failed:', err)
        }
      } else if (todoDocsSettled.status === 'rejected') {
        console.warn('Basecamp todo-documents request failed:', todoDocsSettled.reason)
      }

      if (fileSearchSettled.status === 'fulfilled') {
        try {
          const fileResults = fileSearchSettled.value
          const existingIds = new Set(docResults.results.map((r) => r.id))
          for (const r of fileResults.results) {
            if (!existingIds.has(r.id)) {
              const hit = r as CommandSearchHit
              docResults.results.push({
                ...r,
                _matchType: hit._matchType || "filename",
                _matchScore: hit._matchScore ?? 90,
              })
              existingIds.add(r.id)
            }
          }
        } catch (err) {
          console.warn('Filename search failed:', err)
        }
      }
    } else if (
      searchStrategy === "filename-from-id" &&
      basecampIdToSearch &&
      basecampType === "todo"
    ) {
      // Fast path: indexed todo-documents lookup in parallel with filename search.
      // Do not fall back to full-text content search for bare numeric IDs (12s+ on prod).
      const [todoDocsSettled, fileSearchSettled] = await Promise.allSettled([
        auth(`/api/basecamp/todo-documents/${basecampIdToSearch}?limit=15`),
        runDocumentSearch({
          query: actualSearchQuery,
          limit: 20,
          ...documentSearchOpts,
        }),
      ])

      if (todoDocsSettled.status === "fulfilled" && todoDocsSettled.value.ok) {
        try {
          const basecampData = (await todoDocsSettled.value.json()) as BasecampTodoDocumentsResponse
          mergeTodoDocumentsIntoResults(docResults, basecampData)
        } catch (err) {
          console.warn("Basecamp todo-documents parse failed:", err)
        }
      } else if (todoDocsSettled.status === "rejected") {
        console.warn("Basecamp todo-documents request failed:", todoDocsSettled.reason)
      }

      if (fileSearchSettled.status === "fulfilled") {
        const fileResults = fileSearchSettled.value
        const existingIds = new Set(docResults.results.map((r) => r.id))
        for (const r of fileResults.results) {
          if (!existingIds.has(r.id)) {
            const hit = r as CommandSearchHit
            docResults.results.push({
              ...r,
              _matchType: hit._matchType || "filename",
              _matchScore: hit._matchScore ?? 100,
            })
            existingIds.add(r.id)
          }
        }
      }
    } else {
      docResults = { results: [...(await runDocumentSearch({
        query: actualSearchQuery,
        limit: 20,
        ...documentSearchOpts,
      })).results] }
      docResults.results.forEach((r) => {
        r._matchType = "filename"
        r._matchScore = 100
      })

      if (basecampIdToSearch && docResults.results.length < 10 && basecampType === "todo") {
        try {
          const basecampResponse = await auth(`/api/basecamp/todo-documents/${basecampIdToSearch}`)
          if (basecampResponse.ok) {
            const basecampData = (await basecampResponse.json()) as BasecampTodoDocumentsResponse
            mergeTodoDocumentsIntoResults(docResults, basecampData)
          }
        } catch (err) {
          console.warn("Basecamp ID search failed:", err)
        }
      }
    }
  } else if (!isUrl) {
    docResults = { results: [...(await runDocumentSearch({
      query: trimmedSearch,
      limit: 20,
      ...documentSearchOpts,
    })).results] }
  }

  if (stale()) {
    return {
      allResults: [],
      hasDocumentMatches: false,
      autoSelectFirstFilenameMatch: false,
      searchError: null,
    }
  }

  let projectResults: { projects: Project[] } = { projects: [] }
  const isBareBasecampTodoId = isNumericId && basecampType === "todo" && !isUrl
  const shouldRunProjectSearch =
    !isBasecampTodoUrl &&
    !isBareBasecampTodoId &&
    (!isNumericId || docResults.results.length < 5)

  if (shouldRunProjectSearch) {
    try {
      if (!signal?.aborted && !stale()) {
        projectResults = await searchProjects(trimmedSearch, 5, { signal })
      }
    } catch (err) {
      if (!(signal?.aborted || (err instanceof DOMException && err.name === "AbortError"))) {
        recordSearchError(err)
        console.warn("Project search failed:", err)
      }
    }
  }

  const normalizedSearch = trimmedSearch.toLowerCase()
  const actionBasecampId = basecampIdToSearch || basecampId
  const actionBasecampType = basecampType || "todo"

  const processedResults: CommandItem[] = docResults.results.map((doc) => {
    let filename: string | null = null
    if (doc.filePath) {
      const pathParts = doc.filePath.split("/")
      filename = pathParts[pathParts.length - 1]
    }

    // Use client-side _matchScore if set (filename/basecamp strategies); otherwise
    // fall back to the server's matchScore so normal text searches rank by relevance
    // rather than defaulting to 0 and falling through to alphabetical title sort.
    let matchScore = doc._matchScore ?? doc.matchScore ?? 0
    let matchType = doc._matchType || "normal"

    if (doc.filePath) {
      const pathParts = doc.filePath.split("/").filter((p: string) => p)
      if (pathParts.length >= 2) {
        const folderName = pathParts[pathParts.length - 2].toLowerCase()
        const filenameWithExt = pathParts[pathParts.length - 1]
        const filenameWithoutExt = filenameWithExt.replace(/\.(md|markdown)$/i, "").toLowerCase()

        if (folderName === normalizedSearch && filenameWithoutExt === folderName) {
          matchScore = matchScore + 100
          matchType = "project-root"
        }

        const isReadme = filenameWithExt.toLowerCase() === "readme.md"
        const parentFolderHasBasecampId = actionBasecampId && folderName.includes(actionBasecampId)
        if (isReadme && parentFolderHasBasecampId) {
          matchScore = matchScore + 50
          matchType = "project-root"
        }
      }
    }

    let subtitle = `${doc.type} • ${doc.projectName || "No project"}`
    if (matchType === "project-root") {
      subtitle = `📁 Project Root • ${doc.projectName || "No project"}`
    } else if (matchType === "filename" && filename) {
      subtitle = `📄 ${filename} • ${doc.projectName || "No project"}`
    } else if (matchType === "basecamp-id" && filename) {
      subtitle = `🔗 ${filename} • ${doc.projectName || "No project"}`
    }

    const workspaceBadge =
      workspaces.length > 0 ? workspaceBadgeFromPath(doc.filePath, workspaces) : null

    return {
      id: doc.id || doc.filePath || doc.title,
      title: doc.title,
      type: "file" as const,
      icon: getDocumentIcon(doc.type),
      subtitle,
      action: "open-document",
      matchScore,
      matchType,
      filename: filename ?? undefined,
      filePath: doc.filePath,
      excerpt: doc.excerpt,
      updatedBy: doc.updatedBy,
      updatedAt: doc.updatedAt ?? undefined,
      projectName: doc.projectName,
      docType: doc.type,
      todoId: paletteTodoIdFromDocument({
        filePath: doc.filePath,
        frontmatter: doc.frontmatter,
      }) ?? undefined,
      ...(workspaceBadge ? { workspaceBadge } : {}),
      ...(pastedBasecampUrl ? { basecampSourceUrl: pastedBasecampUrl } : {}),
      ...(basecampUrlInfo.projectId ? { basecampProjectId: basecampUrlInfo.projectId } : {}),
    }
  })

  processedResults.sort((a, b) => {
    const sa = a.matchScore ?? 0
    const sb = b.matchScore ?? 0
    if (sa !== sb) return sb - sa
    return a.title.localeCompare(b.title)
  })

  const basecampWorkSpec = processedResults.find(
    (r) => !!actionBasecampId && r.todoId === actionBasecampId,
  )

  const actionLabels = getActionLabels(actionBasecampType)
  const isFullBasecampUrl = isUrl && !!actionBasecampId && (
    trimmedSearch.includes('basecamp.com') || trimmedSearch.includes('/basecamp/project/')
  )
  const basecampActionScore = isFullBasecampUrl ? 200 : 80

  let resolvedOpenProjectId = basecampUrlInfo.projectId
  if (
    !resolvedOpenProjectId &&
    actionBasecampId &&
    actionBasecampType === 'todo'
  ) {
    try {
      const searchRes = await auth(`/api/basecamp/search-todo/${actionBasecampId}`)
      if (searchRes.ok) {
        const data = (await searchRes.json()) as {
          found?: boolean
          todo?: { bucketId?: string | null; projectId?: string | null }
        }
        const pid = data.todo?.bucketId || data.todo?.projectId
        if (pid) resolvedOpenProjectId = String(pid)
      }
    } catch (err) {
      console.warn('[Command Palette] search-todo lookup for Open action failed:', err)
    }
  }

  const openBasecampScore = resolvedOpenProjectId ? (isFullBasecampUrl ? 198 : 78) : 0

  const openBasecampRow: CommandItem[] =
    actionBasecampId &&
    actionBasecampType === 'todo' &&
    resolvedOpenProjectId
      ? [
          {
            id: `open-basecamp-${actionBasecampId}`,
            title: 'Open in Basecamp',
            type: 'action' as const,
            icon: <Link2 className="h-4 w-4" />,
            subtitle: 'View todo in Basecamp integration tab',
            action: 'open-basecamp-todo',
            matchScore: openBasecampScore,
            basecampId: actionBasecampId,
            basecampProjectId: resolvedOpenProjectId,
            basecampSourceUrl: pastedBasecampUrl ?? trimmedSearch,
            basecampType: 'todo',
          },
        ]
      : []

  let maaterOpenRows: CommandItem[] = []
  if (maaterUrlInfo && !stale()) {
    const resolvedMaater = await resolveMaaterDocumentByPath(
      maaterUrlInfo.pathOrId,
      workspaces,
      auth,
      signal,
    )
    if (!stale()) {
      maaterOpenRows = buildMaaterDocumentOpenRows({
        pathOrId: maaterUrlInfo.pathOrId,
        resolved: resolvedMaater,
        workspaces,
        activeWorkspaceIds,
      })
    }
  }

  const maaterOpenIds = new Set(
    maaterOpenRows.flatMap((r) => [r.id, r.existingDocId].filter(Boolean) as string[]),
  )
  const docsWithoutMaaterDupes = processedResults.filter(
    (r) => !maaterOpenIds.has(r.id) && !(r.filePath && maaterUrlInfo && r.filePath.includes(maaterUrlInfo.pathOrId)),
  )

  const allResults: CommandItem[] = [
    ...maaterOpenRows,
    ...(isFullBasecampUrl && actionBasecampId && basecampWorkSpec
      ? [
          {
            id: `update-basecamp-${actionBasecampId}`,
            title: actionLabels.update,
            type: "action" as const,
            icon: <RefreshCw className="h-4 w-4" />,
            subtitle: `Re-fetch and update: ${basecampWorkSpec.title}`,
            action:
              actionBasecampType === "message" || actionBasecampType === "inbox_forward"
                ? "update-basecamp-message"
                : "update-basecamp-todo",
            matchScore: basecampActionScore,
            basecampId: actionBasecampId,
            basecampType: actionBasecampType,
            existingDocId: basecampWorkSpec.id,
          },
        ]
      : []),
    ...openBasecampRow,
    ...(isFullBasecampUrl && actionBasecampId && !basecampWorkSpec
      ? [
          {
            id: `ingest-basecamp-${actionBasecampId}`,
            title: actionLabels.ingest,
            type: "action" as const,
            icon: <Hash className="h-4 w-4" />,
            subtitle: actionLabels.subtitle,
            action:
              actionBasecampType === "message" || actionBasecampType === "inbox_forward"
                ? "ingest-basecamp-message"
                : "ingest-basecamp-todo",
            matchScore: basecampActionScore,
            basecampId: actionBasecampId,
            basecampType: actionBasecampType,
          },
        ]
      : []),
    ...docsWithoutMaaterDupes,
    ...(!isFullBasecampUrl && actionBasecampId && basecampWorkSpec
      ? [
          {
            id: `update-basecamp-${actionBasecampId}`,
            title: actionLabels.update,
            type: "action" as const,
            icon: <RefreshCw className="h-4 w-4" />,
            subtitle: `Re-fetch and update: ${basecampWorkSpec.title}`,
            action:
              actionBasecampType === "message" || actionBasecampType === "inbox_forward"
                ? "update-basecamp-message"
                : "update-basecamp-todo",
            matchScore: basecampActionScore,
            basecampId: actionBasecampId,
            basecampType: actionBasecampType,
            existingDocId: basecampWorkSpec.id,
          },
        ]
      : []),
    ...(!isFullBasecampUrl && actionBasecampId && !basecampWorkSpec
      ? [
          {
            id: `ingest-basecamp-${actionBasecampId}`,
            title: `${actionLabels.ingest} ${actionBasecampId}`,
            type: "action" as const,
            icon: <Hash className="h-4 w-4" />,
            subtitle: actionLabels.subtitle,
            action:
              actionBasecampType === "message" || actionBasecampType === "inbox_forward"
                ? "ingest-basecamp-message"
                : "ingest-basecamp-todo",
            matchScore: basecampActionScore,
            basecampId: actionBasecampId,
            basecampType: actionBasecampType,
          },
        ]
      : []),
    ...(!isNumericId || docResults.results.length < 5
      ? projectResults.projects.map((project) => ({
          id: project.id,
          title: project.name,
          type: "file" as const,
          icon: <Folder className="h-4 w-4" />,
          subtitle: `${project.documentCount} documents`,
          action: "open-project",
          priority: "project",
        }))
      : []),
  ]

  allResults.sort((a, b) => {
    const scoreA = a.matchScore || 0
    const scoreB = b.matchScore || 0
    if (scoreA !== scoreB) return scoreB - scoreA
    if (a.type === "action" && b.type !== "action") return -1
    if (a.type !== "action" && b.type === "action") return 1
    return a.title.localeCompare(b.title)
  })

  const hasDocumentMatches = docsWithoutMaaterDupes.length > 0 || maaterOpenRows.length > 0
  let autoSelectFirstFilenameMatch = false
  if (
    allResults.length > 0 &&
    (extractedFilename || /\.(md|markdown)$/i.test(trimmedSearch))
  ) {
    const firstResult = allResults[0]
    if (firstResult.matchType === "filename" || firstResult.matchType === "basecamp-id") {
      autoSelectFirstFilenameMatch = true
    }
  }

  let preferredDocumentIndex: number | undefined
  if (maaterOpenRows.length > 0) {
    preferredDocumentIndex = allResults.findIndex(
      (r) =>
        r.matchType === 'maater-url' ||
        r.action === 'enable-workspace-and-open' ||
        (r.action === 'open-document' && maaterOpenIds.has(r.id)),
    )
  } else if (isFullBasecampUrl && actionBasecampId) {
    preferredDocumentIndex = allResults.findIndex(
      (r) =>
        r.action === 'ingest-basecamp-message' ||
        r.action === 'ingest-basecamp-todo' ||
        r.action === 'update-basecamp-message' ||
        r.action === 'update-basecamp-todo',
    )
  } else if (isBasecampTodoUrl) {
    preferredDocumentIndex = allResults.findIndex((r) => r.action === 'open-document')
  }

  return {
    allResults,
    hasDocumentMatches,
    autoSelectFirstFilenameMatch,
    preferredDocumentIndex,
    searchError,
  }
}
